import { BookUser, Building2, CalendarRange, CalendarDays, FileText, Flame, Globe, KeyRound, Library, Music2, Package, PenLine, Shield, ShoppingCart, Users, Users2, Wrench } from 'lucide-react';
import authorizationRegistry from '../generated/authorizationRegistry.json';
import { MODULE_ID_ALIASES, canonicalizeModuleId } from './moduleAccess';

// Authorization metadata (id/label/description/order/access permission) is
// generated from the backend registry. Only browser presentation details live
// here: route, icon and restricted-shell hint.
const MODULE_PRESENTATION = {
  worship: { homeRoute: '/worship', icon: Music2, restrictedFlag: 'restricted_to_worship' },
  connectlessen: { homeRoute: '/connectlessen', icon: Users, restrictedFlag: 'restricted_to_connectlessen' },
  reserveringen: { homeRoute: '/bookings', icon: CalendarDays },
  ledenbeheer: { homeRoute: '/members', icon: BookUser },
  connectgroepen: { homeRoute: '/connectgroepen', icon: Users2 },
  gebouwbeheer: { homeRoute: '/buildings', icon: Building2 },
  voorraadbeheer: { homeRoute: '/inventory', icon: Package },
  sleutelbeheer: { homeRoute: '/keys', icon: KeyRound },
  central_planning: { homeRoute: '/planning', icon: CalendarRange },
  kinderwerk: { homeRoute: '/kinderwerk', icon: Users, restrictedFlag: 'restricted_to_kinderwerk' },
  commerce: { homeRoute: '/commerce', icon: ShoppingCart },
  heating: { homeRoute: '/heating', icon: Flame },
  contractbeheer: { homeRoute: '/contracts', icon: FileText },
  bibliotheek: { homeRoute: '/library', icon: Library },
  writer_studio: { homeRoute: '/writer', icon: PenLine },
  leiderschap: { homeRoute: '/leiderschap', icon: Shield },
  sprekers: { homeRoute: '/sprekers', icon: Wrench },
  sites: { homeRoute: '/sites', icon: Globe },
};

export const MODULE_REGISTRY = authorizationRegistry.modules
  .map((module) => ({
    ...module,
    ...(MODULE_PRESENTATION[module.id] || {}),
  }))
  .filter((module) => module.homeRoute);

const dedupe = (modules) => Array.from(new Map(modules.map((module) => [module.id, module])).values());

export const getUnmappedModuleIds = (moduleIds = []) => Array.from(new Set(moduleIds))
  .filter((moduleId) => !MODULE_REGISTRY.some((module) => module.id === moduleId)
    && !Object.prototype.hasOwnProperty.call(MODULE_ID_ALIASES, moduleId));

export const getEffectiveAccessibleModules = (user) => {
  if (!user) return [];

  const projectedAccess = user.effective_access;
  if (projectedAccess && typeof projectedAccess === 'object' && !Array.isArray(projectedAccess)) {
    const accessibleIds = Object.entries(projectedAccess)
      .filter(([, state]) => state?.access === true)
      .map(([moduleId]) => canonicalizeModuleId(moduleId));
    const unmapped = getUnmappedModuleIds(accessibleIds);
    if (unmapped.length && process.env.NODE_ENV !== 'production') {
      console.warn(`[moduleRegistry] No frontend home route registered for: ${unmapped.join(', ')}`);
    }
    return dedupe(MODULE_REGISTRY.filter((module) => accessibleIds.includes(module.id)));
  }

  if (Array.isArray(user.effective_modules)) {
    const accessibleIds = user.effective_modules.map((moduleId) => canonicalizeModuleId(moduleId));
    return dedupe(MODULE_REGISTRY.filter((module) => accessibleIds.includes(module.id)));
  }

  return [];
};

export const getModuleHomeRoute = (moduleId) => MODULE_REGISTRY.find((module) => module.id === moduleId)?.homeRoute;

export const hasEffectiveModuleAccess = (user, moduleId) => getEffectiveAccessibleModules(user)
  .some((module) => module.id === canonicalizeModuleId(moduleId));
